import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  MapPin, Facebook, Instagram, 
  Star, Edit2, AlertCircle, BookmarkIcon,
  History, Users, Calendar, ChevronLeft, 
  ChevronRight, ArrowRight, Share2, Mail, Phone,
  Eye, Heart, ThumbsUp, MessageCircle
} from 'lucide-react';
import PropertyCard from '../../components/PropertyCard';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { useToast } from '../../components/ui/toast/use-toast';

// Constants
const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';
const CLOUDFLARE_BANNERS_URL = 'https://pub-cdaec3d9feef44cf8950eee9a93266e4.r2.dev';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.8, 
      ease: [0.25, 0.46, 0.45, 0.94]
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const cardHover = {
  rest: { scale: 1, y: 0 },
  hover: { 
    scale: 1.02, 
    y: -8,
    transition: { 
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Utility Components
const ProfileAvatar = ({ src, size = "md", className = "", userData = null, navigate }) => {
  const [error, setError] = useState(false);
  
  const sizes = {
    sm: "w-16 h-16",
    md: "w-32 h-32 md:w-40 md:h-40",
    lg: "w-40 h-40 md:w-52 md:h-52"
  };

  const getProfileImageUrl = (imageUrl) => {
    if (!imageUrl) return '/default-profile.png';
    
    const cleanUrl = imageUrl
      .replace('https://backend-production-1786.up.railway.app/api/profiles/', '')
      .replace('/profiles/', '')
      .replace('profiles/', '')
      .replace(/^\//, '');
    
    return `${CLOUDFLARE_PROFILES_URL}/${cleanUrl}`;
  };

  const handleProfileClick = () => {
    if (!userData || userData.tipo_usuario_id === 1) return;

    const userTypeRoutes = {
      2: 'vendedor',
      3: 'constructora',
      4: 'inmobiliaria'
    };

    const userType = userTypeRoutes[userData.tipo_usuario_id];
    if (userType && userData.nombre_usuario) {
      navigate(`/${userType}/${userData.nombre_usuario}`);
    }
  };

  const imageUrl = error ? '/default-profile.png' : getProfileImageUrl(src);

  return (
    <motion.div 
      className={`relative ${sizes[size]} ${className}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, type: "spring", damping: 15 }}
      whileHover={{ scale: 1.08 }}
      onClick={handleProfileClick}
    >
      <motion.div 
        className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600 p-1"
        whileHover={{
          rotate: 360,
          transition: { duration: 1, ease: "easeInOut" }
        }}
      >
        <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 p-1">
          <img 
            src={imageUrl}
            alt="Profile" 
            className="w-full h-full rounded-full object-cover"
            onError={() => setError(true)}
          />
        </div>
      </motion.div>
      
      <motion.div 
        className="absolute bottom-1 right-1 md:bottom-2 md:right-2 w-3 h-3 md:w-4 md:h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5, type: "spring" }}
      />
    </motion.div>
  );
};

const ActionButton = ({ 
  children, 
  onClick, 
  variant = "primary", 
  size = "md", 
  icon: Icon,
  className = "",
  ...props 
}) => {
  const variants = {
    primary: "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl",
    secondary: "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600",
    ghost: "bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white border border-white/20 dark:border-white/10 hover:bg-white/20 dark:hover:bg-white/10"
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm md:px-6 md:py-3 md:text-base",
    lg: "px-6 py-3 text-base md:px-8 md:py-4 md:text-lg"
  };

  return (
    <motion.button
      className={`
        ${variants[variant]} 
        ${sizes[size]} 
        rounded-xl font-medium transition-all duration-300 
        flex items-center justify-center gap-2 
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
        ${className}
      `}
      onClick={onClick}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.98 }}
      {...props}
    >
      {Icon && <Icon className="w-4 h-4 md:w-5 md:h-5" />}
      {children}
    </motion.button>
  );
};

const SocialLink = ({ href, icon: Icon, platform, color, variant = "banner" }) => {
  const baseClasses = "p-2 md:p-3 rounded-lg md:rounded-xl text-white transition-all duration-300";
  const bannerClasses = "backdrop-blur-sm border border-white/20 dark:border-white/10";
  const mobileClasses = "shadow-lg dark:shadow-xl";
  
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`
        ${baseClasses}
        ${variant === "banner" ? bannerClasses : mobileClasses}
        ${color} hover:scale-110 hover:rotate-3
      `}
      whileHover={{ scale: 1.1, rotate: 3 }}
      whileTap={{ scale: 0.95 }}
      title={platform}
    >
      <Icon className="w-4 h-4 md:w-5 md:h-5" />
    </motion.a>
  );
};

const StatCard = ({ icon: Icon, value, label, color = "blue" }) => {
  const colorClasses = {
    blue: "from-blue-500 to-purple-600",
    indigo: "from-blue-600 to-indigo-600",
    purple: "from-purple-500 to-blue-600",
    cyan: "from-blue-400 to-cyan-500"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-3 md:p-6 shadow-lg dark:shadow-black/50 border border-blue-100 dark:border-gray-700 overflow-hidden relative"
      variants={cardHover}
      initial="rest"
      whileHover="hover"
    >
      <div className={`absolute top-0 right-0 w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br ${colorClasses[color]} opacity-10 rounded-full -translate-y-3 translate-x-3 md:-translate-y-6 md:translate-x-6`} />
      
      <div className="relative flex items-center gap-2 md:gap-4">
        <div className={`p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg flex-shrink-0`}>
          <Icon className="w-4 h-4 md:w-6 md:h-6 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-lg md:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-gray-200 leading-tight">{value}</div>
          <div className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 leading-tight">{label}</div>
        </div>
      </div>
    </motion.div>
  );
};

const PropertySlider = ({ 
  properties, 
  title, 
  icon: Icon, 
  onViewAll = null,
  emptyMessage = "No hay propiedades para mostrar",
  viewAllText = "Ver todas"
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSlidesPerView(1);
      } else if (window.innerWidth < 1024) {
        setSlidesPerView(2);
      } else {
        setSlidesPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxSlides = Math.max(0, Math.ceil(properties.length / slidesPerView) - 1);

  if (properties.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
          <Icon className="w-8 h-8 text-blue-500 dark:text-blue-400" />
        </div>
        <p className="text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <motion.div 
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-6 md:p-8 border border-blue-100 dark:border-gray-700"
      variants={fadeInUp}
    >
      {title && (
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">{title}</h2>
        </div>
      )}

      <div className="relative">
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
          disabled={currentSlide === 0}
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full shadow-lg dark:shadow-black/50 transition-all duration-300 ${
            currentSlide === 0 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
              : 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 hover:shadow-xl dark:hover:shadow-black/70'
          } hidden md:flex items-center justify-center border border-gray-200 dark:border-gray-600`}
          style={{ transform: 'translateX(-50%) translateY(-50%)' }}
          whileHover={currentSlide !== 0 ? { scale: 1.1 } : {}}
          whileTap={currentSlide !== 0 ? { scale: 0.95 } : {}}
        >
          <ChevronLeft className="w-5 h-5" />
        </motion.button>
        
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.min(maxSlides, prev + 1))}
          disabled={currentSlide >= maxSlides}
          className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full shadow-lg dark:shadow-black/50 transition-all duration-300 ${
            currentSlide >= maxSlides 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
              : 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 hover:shadow-xl dark:hover:shadow-black/70'
          } hidden md:flex items-center justify-center border border-gray-200 dark:border-gray-600`}
          style={{ transform: 'translateX(50%) translateY(-50%)' }}
          whileHover={currentSlide < maxSlides ? { scale: 1.1 } : {}}
          whileTap={currentSlide < maxSlides ? { scale: 0.95 } : {}}
        >
          <ChevronRight className="w-5 h-5" />
        </motion.button>

        <div className="overflow-hidden mx-4 md:mx-8">
          <motion.div 
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentSlide * (100 / slidesPerView)}%)` }}
          >
            {properties.map((property) => (
              <motion.div 
                key={property.id} 
                className="flex-shrink-0 px-2"
                style={{ width: `${100 / slidesPerView}%` }}
                variants={cardHover}
                initial="rest"
                whileHover="hover"
              >
                <PropertyCard property={property} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="flex items-center justify-center gap-4 mt-6 md:hidden">
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
          disabled={currentSlide === 0}
          className={`p-2 rounded-lg transition-colors ${
            currentSlide === 0 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500' 
              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'
          }`}
          whileHover={currentSlide !== 0 ? { scale: 1.1 } : {}}
          whileTap={currentSlide !== 0 ? { scale: 0.95 } : {}}
        >
          <ChevronLeft className="w-5 h-5" />
        </motion.button>
        
        <div className="flex gap-2">
          {Array.from({ length: maxSlides + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentSlide ? 'bg-blue-600 dark:bg-blue-400' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            />
          ))}
        </div>
        
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.min(maxSlides, prev + 1))}
          disabled={currentSlide >= maxSlides}
          className={`p-2 rounded-lg transition-colors ${
            currentSlide >= maxSlides 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500' 
              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'
          }`}
          whileHover={currentSlide < maxSlides ? { scale: 1.1 } : {}}
          whileTap={currentSlide < maxSlides ? { scale: 0.95 } : {}}
        >
          <ChevronRight className="w-5 h-5" />
        </motion.button>
      </div>

      {onViewAll && (
        <div className="flex justify-center mt-6">
          <ActionButton
            variant="primary"
            size="md"
            onClick={onViewAll}
            icon={ArrowRight}
          >
            {viewAllText}
          </ActionButton>
        </div>
      )}
    </motion.div>
  );
};

const PartnersCarousel = ({ partners, slidesPerView = 3 }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(slidesPerView);
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxSlides = Math.ceil(partners.length / itemsPerView);
  
  const getUserTypeLabel = (typeId) => {
    const tipos = {
      1: 'CLIENTE',
      2: 'VENDEDOR',
      3: 'CONSTRUCTORA',
      4: 'INMOBILIARIA'
    };
    return tipos[typeId] || 'CLIENTE';
  };

  return (
    <div className="relative mb-6">
      <div className="overflow-hidden mb-6">
        <motion.div 
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentSlide * (100 / itemsPerView)}%)` }}
        >
          {partners.map((partner) => (
            <motion.div
              key={partner.id}
              className="w-full flex-shrink-0 px-3"
              style={{ width: `${100 / itemsPerView}%` }}
              variants={cardHover}
              initial="rest"
              whileHover="hover"
            >
              <motion.div
                className="bg-gradient-to-br from-white to-blue-50/30 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 shadow-lg dark:shadow-black/50 border border-blue-100/50 dark:border-gray-700 h-full cursor-pointer"
                onClick={() => {
                  const userTypes = {
                    2: 'vendedor',
                    3: 'constructora',
                    4: 'inmobiliaria'
                  };
                  const userType = userTypes[partner.tipo_usuario_id];
                  if (userType) {
                    navigate(`/${userType}/${partner.nombre_usuario}`);
                  }
                }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <ProfileAvatar 
                    src={partner.foto_perfil}
                    size="sm"
                    userData={partner}
                    navigate={navigate}
                  />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-800 dark:text-gray-200">{partner.nombre}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{getUserTypeLabel(partner.tipo_usuario_id)}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < partner.calificacion 
                            ? 'fill-yellow-400 text-yellow-400' 
                            : 'text-gray-300 dark:text-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-100 dark:border-gray-700">
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                    <span>Propiedades:</span>
                    <span className="font-medium">{partner.total_propiedades}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <span>Calificación:</span>
                    <span className="font-medium">{parseFloat(partner.calificacion_promedio || 0).toFixed(1)}/5.0</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <div className="flex items-center justify-center gap-4">
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
          disabled={currentSlide === 0}
          className={`p-3 rounded-full ${
            currentSlide === 0 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
              : 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-md dark:shadow-black/50 hover:shadow-lg dark:hover:shadow-black/70 hover:bg-blue-50 dark:hover:bg-gray-700'
          }`}
          whileHover={currentSlide !== 0 ? { scale: 1.1 } : {}}
          whileTap={currentSlide !== 0 ? { scale: 0.95 } : {}}
        >
          <ChevronLeft className="w-6 h-6" />
        </motion.button>
        
        <span className="text-gray-600 dark:text-gray-400 font-medium">
          {currentSlide + 1} / {maxSlides || 1}
        </span>
        
        <motion.button
          onClick={() => setCurrentSlide(prev => Math.min(maxSlides - 1, prev + 1))}
          disabled={currentSlide >= maxSlides - 1}
          className={`p-3 rounded-full ${
            currentSlide >= maxSlides - 1 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
              : 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-md dark:shadow-black/50 hover:shadow-lg dark:hover:shadow-black/70 hover:bg-blue-50 dark:hover:bg-gray-700'
          }`}
          whileHover={currentSlide < maxSlides - 1 ? { scale: 1.1 } : {}}
          whileTap={currentSlide < maxSlides - 1 ? { scale: 0.95 } : {}}
        >
          <ChevronRight className="w-6 h-6" />
        </motion.button>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <motion.button
    onClick={onClick}
    className={`
      px-4 py-2 md:px-6 md:py-3 rounded-xl font-medium transition-all duration-300 
      flex items-center gap-2 whitespace-nowrap
      ${active 
        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg dark:shadow-xl' 
        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 border border-blue-200 dark:border-gray-600'}
    `}
    whileHover={{ scale: active ? 1 : 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </motion.button>
);

// Main Component
const ClientProfile = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Estados
  const [activeTab, setActiveTab] = useState('resumen');
  const [loading, setLoading] = useState({
    profile: true,
    favorites: true,
    history: true,
    partners: true
  });
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    userData: null,
    favorites: [],
    history: [],
    topPartners: [],
    favoritesCount: 0
  });

  const isOwnProfile = location.pathname === '/mi_perfil';

  const getUserTypeLabel = (typeId) => {
    const tipos = {
      1: 'CLIENTE',
      2: 'VENDEDOR',
      3: 'CONSTRUCTORA',
      4: 'INMOBILIARIA'
    };
    return tipos[typeId] || 'CLIENTE';
  };

  // Data fetching
  useEffect(() => {
    let isSubscribed = true;
    
    const fetchData = async (url, options = {}) => {
      try {
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options.headers
          },
          ...options
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return await response.json();
      } catch (error) {
        console.error(`Error fetching ${url}:`, error);
        throw error;
      }
    };

    const loadData = async () => {
      if (!token) {
        logout();
        navigate('/login');
        return;
      }

      try {
        const requests = [
          {
            key: 'userData',
            url: 'https://backend-production-1786.up.railway.app/api/vendedores/perfil',
            loadingKey: 'profile'
          },
          {
            key: 'favorites',
            url: 'https://backend-production-1786.up.railway.app/api/favoritos',
            loadingKey: 'favorites'
          },
          {
            key: 'history',
            url: 'https://backend-production-1786.up.railway.app/api/historial-visitas',
            loadingKey: 'history',
            process: data => data.slice(0, 9)
          },
          {
            key: 'topPartners',
            url: 'https://backend-production-1786.up.railway.app/api/socios/top-rated',
            loadingKey: 'partners'
          },
          {
            key: 'favoritesCount',
            url: 'https://backend-production-1786.up.railway.app/api/favoritos/count',
            process: data => data.count
          }
        ];

        const results = await Promise.allSettled(
          requests.map(request => fetchData(request.url))
        );

        if (isSubscribed) {
          results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              const request = requests[index];
              const value = request.process ? request.process(result.value) : result.value;
              setData(prev => ({ ...prev, [request.key]: value }));
              if (request.loadingKey) {
                setLoading(prev => ({ ...prev, [request.loadingKey]: false }));
              }
            }
          });
        }
      } catch (error) {
        if (isSubscribed) {
          setError(error.message);
          if (error.message.includes('401')) {
            logout();
            navigate('/login');
          }
        }
      }
    };

    loadData();
    return () => { isSubscribed = false; };
  }, [token, logout, navigate]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Perfil de ${data.userData?.nombre}`,
          text: data.userData?.sobre_mi || "Perfil de Cliente en Kairos",
          url: window.location.href
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: "¡Enlace copiado!",
          description: "El enlace del perfil ha sido copiado al portapapeles.",
          variant: "success",
        });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
  };

  const getBannerImageUrl = (imageUrl) => {
    if (!imageUrl) return '/default-banner.jpg';
    
    if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/https://pub-') && imageUrl.includes('r2.dev')) {
      return imageUrl.substring(imageUrl.indexOf('https://pub-'));
    }
    
    if (imageUrl.includes('r2.dev')) {
      return imageUrl;
    }
    
    if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/')) {
      const filename = imageUrl.split('/banners/').pop();
      return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
    }
    
    if (imageUrl.includes('/banners/')) {
      const filename = imageUrl.split('/banners/').pop();
      return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
    }
    
    return `${CLOUDFLARE_BANNERS_URL}/${imageUrl}`;
  };

  const stats = useMemo(() => [
    {
      label: "Favoritos",
      value: data.favoritesCount,
      icon: BookmarkIcon,
      color: "blue"
    },
    {
      label: "Historial",
      value: data.history.length,
      icon: History,
      color: "indigo"
    },
    {
      label: "Socios",
      value: data.topPartners.length,
      icon: Users,
      color: "purple"
    },
    {
      label: "Días en Kairos",
      value: data.userData?.fecha_registro ? 
        Math.floor((new Date() - new Date(data.userData.fecha_registro)) / (1000 * 60 * 60 * 24)) : 0,
      icon: Calendar,
      color: "cyan"
    }
  ], [data.favoritesCount, data.history.length, data.topPartners.length, data.userData]);

  // Loading state
  if (Object.values(loading).every(val => val)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <motion.div
          className="relative"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <div className="w-24 h-24 border-4 border-blue-200 dark:border-blue-800 rounded-full">
            <div className="absolute top-0 left-0 w-24 h-24 border-4 border-blue-600 dark:border-blue-400 rounded-full border-t-transparent animate-spin"></div>
          </div>
        </motion.div>
        <motion.div
          className="text-center"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <p className="text-xl font-semibold text-blue-700 dark:text-blue-300 mb-2">Cargando perfil...</p>
          <p className="text-gray-600 dark:text-gray-400">Obteniendo información del cliente</p>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-white dark:from-gray-900 dark:to-gray-800 px-4">
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl dark:shadow-black/50 p-8 max-w-md w-full text-center border border-gray-100 dark:border-gray-700"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-red-100 dark:bg-red-900/30 w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-red-500 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">
            Error al cargar el perfil
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <ActionButton
            variant="primary"
            onClick={() => navigate(-1)}
            className="w-full"
          >
            Volver atrás
          </ActionButton>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Hero Banner */}
      <div className="relative h-80 md:h-80 lg:h-96 overflow-hidden">
        <motion.div 
          className="absolute inset-0"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          {data.userData?.banner_image && (
            <img 
              src={getBannerImageUrl(data.userData.banner_image)}
              alt="Banner"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
              }}
            />
          )}
          
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/70 to-purple-900/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white rounded-full opacity-30"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </motion.div>
        </motion.div>

        {/* Profile Content */}
        <div className="absolute inset-0 flex items-center justify-center p-6 md:p-4">
          <div className="max-w-7xl mx-auto w-full">
            {/* Mobile Layout */}
            <div className="flex flex-col items-center text-center md:hidden">
              <ProfileAvatar 
                src={data.userData?.foto_perfil}
                size="md"
                className="mb-4 mt-10"
                userData={data.userData}
                navigate={navigate}
              />
              
              <motion.h1 
                className="text-2xl font-bold text-white mb-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {data.userData?.nombre}
              </motion.h1>
              
              <motion.div 
                className="flex items-center gap-2 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <span className="px-3 py-1 bg-white/20 dark:bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/30 dark:border-white/20">
                  {getUserTypeLabel(data.userData?.tipo_usuario_id)}
                </span>
              </motion.div>
              
              <motion.div 
                className="flex items-center text-white/90 text-sm mb-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <MapPin className="w-4 h-4 mr-1" />
                {data.userData?.ubicacion || 'Sin ubicación'}
              </motion.div>

              <motion.div 
                className="flex flex-wrap gap-2 md:gap-3 justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                {isOwnProfile && (
                  <ActionButton
                    variant="ghost"
                    size="sm"
                    icon={Edit2}
                    onClick={() => navigate('/edit-profile')}
                  >
                    Editar
                  </ActionButton>
                )}
                
                <motion.button
                  onClick={handleShare}
                  className="p-2 md:p-3 rounded-lg md:rounded-xl bg-white/20 dark:bg-white/10 backdrop-blur-sm text-white border border-white/20 dark:border-white/10 hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Share2 className="w-4 h-4 md:w-5 md:h-5" />
                </motion.button>
              </motion.div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-end gap-8">
              <ProfileAvatar 
                src={data.userData?.foto_perfil}
                size="lg"
                userData={data.userData}
                navigate={navigate}
              />
              
              <div className="flex-1">
                <motion.h1 
                  className="text-4xl lg:text-5xl font-bold text-white mb-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {data.userData?.nombre}
                </motion.h1>
                
                <motion.div 
                  className="flex items-center gap-4 mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <span className="px-4 py-2 bg-white/20 dark:bg-white/10 backdrop-blur-sm text-white rounded-full font-medium border border-white/30 dark:border-white/20">
                    {getUserTypeLabel(data.userData?.tipo_usuario_id)}
                  </span>
                  <div className="flex items-center text-white/90">
                    <MapPin className="w-5 h-5 mr-2" />
                    {data.userData?.ubicacion || 'Sin ubicación'}
                  </div>
                </motion.div>
              </div>
              
              <motion.div 
                className="flex gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                {isOwnProfile && (
                  <ActionButton
                    variant="ghost"
                    size="md"
                    icon={Edit2}
                    onClick={() => navigate('/edit-profile')}
                  >
                    Editar Perfil
                  </ActionButton>
                )}
                
                <div className="flex gap-2">
                  {data.userData?.email && (
                    <SocialLink 
                      href={`mailto:${data.userData.email}`}
                      icon={Mail}
                      platform="Email"
                      color="bg-blue-500/70 hover:bg-blue-500"
                      variant="banner"
                    />
                  )}
                  {data.userData?.telefono && (
                    <SocialLink 
                      href={`tel:${data.userData.telefono}`}
                      icon={Phone}
                      platform="Teléfono"
                      color="bg-blue-500/70 hover:bg-blue-500"
                      variant="banner"
                    />
                  )}
                  {data.userData?.facebook && (
                    <SocialLink 
                      href={`https://facebook.com/${data.userData.facebook}`}
                      icon={Facebook}
                      platform="Facebook"
                      color="bg-blue-600/70 hover:bg-blue-600"
                      variant="banner"
                    />
                  )}
                  {data.userData?.instagram && (
                    <SocialLink 
                      href={`https://instagram.com/${data.userData.instagram}`}
                      icon={Instagram}
                      platform="Instagram"
                      color="bg-purple-600/70 hover:bg-purple-600"
                      variant="banner"
                    />
                  )}
                  
                  <motion.button
                    onClick={handleShare}
                    className="p-2 md:p-3 rounded-lg md:rounded-xl bg-white/20 dark:bg-white/10 backdrop-blur-sm text-white border border-white/20 dark:border-white/10 hover:bg-white/30 dark:hover:bg-white/20 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Share2 className="w-4 h-4 md:w-5 md:h-5" />
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Slogan Bar */}
        <motion.div 
          className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm border-t border-white/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <div className="max-w-7xl mx-auto px-4 py-4">
            <p className="text-white text-center font-medium text-lg">
              {data.userData?.sobre_mi || "Explorando el mundo inmobiliario en Kairos"}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Mobile Social Links & Actions */}
      <motion.div 
        className="md:hidden bg-white dark:bg-gray-800 shadow-lg dark:shadow-black/50 border-b border-gray-100 dark:border-gray-700 py-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {isOwnProfile && (
              <ActionButton
                variant="secondary"
                size="sm"
                icon={Edit2}
                onClick={() => navigate('/edit-profile')}
                className="flex-shrink-0"
              >
                Editar
              </ActionButton>
            )}
            
            <motion.button
              onClick={handleShare}
              className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 flex items-center gap-2 text-sm flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Share2 className="w-4 h-4" />
              Compartir
            </motion.button>
            
            {data.userData?.email && (
              <SocialLink 
                href={`mailto:${data.userData.email}`}
                icon={Mail}
                platform="Email"
                color="bg-blue-500 hover:bg-blue-600"
                variant="mobile"
              />
            )}
            {data.userData?.telefono && (
              <SocialLink 
                href={`tel:${data.userData.telefono}`}
                icon={Phone}
                platform="Teléfono"
                color="bg-blue-500 hover:bg-blue-600"
                variant="mobile"
              />
            )}
            {data.userData?.facebook && (
              <SocialLink 
                href={`https://facebook.com/${data.userData.facebook}`}
                icon={Facebook}
                platform="Facebook"
                color="bg-blue-600 hover:bg-blue-700"
                variant="mobile"
              />
            )}
            {data.userData?.instagram && (
              <SocialLink 
                href={`https://instagram.com/${data.userData.instagram}`}
                icon={Instagram}
                platform="Instagram"
                color="bg-purple-600 hover:bg-purple-700"
                variant="mobile"
              />
            )}
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        {/* Stats Grid */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-12"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.8 }}
        >
          {stats.map((stat, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <StatCard {...stat} />
            </motion.div>
          ))}
        </motion.div>

        {/* About Section */}
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-6 md:p-8 mb-8 md:mb-12 border border-blue-100 dark:border-gray-700"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
              Acerca de mí
            </h2>
          </div>
          
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm md:text-base">
            {data.userData?.sobre_mi || 
            'Apasionado por encontrar el hogar perfecto. Mi objetivo es descubrir propiedades que se ajusten a mi estilo de vida y necesidades, siempre atento a oportunidades inmobiliarias que ofrezcan valor y calidad.'}
          </p>
        </motion.div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 md:gap-3 mb-8 overflow-x-auto pb-2 scrollbar-hide">
          <TabButton 
            active={activeTab === 'resumen'} 
            onClick={() => setActiveTab('resumen')}
            icon={Users}
          >
            Resumen
          </TabButton>
          <TabButton 
            active={activeTab === 'favorites'} 
            onClick={() => setActiveTab('favorites')}
            icon={BookmarkIcon}
          >
            Favoritos ({data.favoritesCount})
          </TabButton>
          <TabButton 
            active={activeTab === 'history'} 
            onClick={() => setActiveTab('history')}
            icon={History}
          >
            Historial
          </TabButton>
          <TabButton 
            active={activeTab === 'partners'} 
            onClick={() => setActiveTab('partners')}
            icon={Users}
          >
            Socios ({data.topPartners.length})
          </TabButton>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'resumen' && (
            <motion.div
              key="resumen"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              {data.favorites.length > 0 && (
                <PropertySlider 
                  properties={data.favorites}
                  title="Propiedades Guardadas"
                  icon={BookmarkIcon}
                  onViewAll={() => setActiveTab('favorites')}
                />
              )}

              {data.history.length > 0 && (
                <PropertySlider 
                  properties={data.history}
                  title="Historial de Visitas"
                  icon={History}
                  onViewAll={() => setActiveTab('history')}
                />
              )}

              {data.topPartners.length > 0 && (
                <motion.div 
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-8 border border-blue-100 dark:border-gray-700"
                  variants={fadeInUp}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">Socios Favoritos</h2>
                  </div>
                  <PartnersCarousel partners={data.topPartners} />
                  <div className="flex justify-center mt-6">
                    <ActionButton
                      variant="primary"
                      size="md"
                      onClick={() => setActiveTab('partners')}
                      icon={ArrowRight}
                    >
                      Ver todos los socios
                    </ActionButton>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {activeTab === 'favorites' && (
            <motion.div
              key="favorites"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-6 md:p-8 border border-blue-100 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
                    <BookmarkIcon className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
                    Propiedades Guardadas
                  </h2>
                </div>
                
                {data.favorites.length > 0 ? (
                  <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    {data.favorites.map((property) => (
                      <motion.div 
                        key={property.id} 
                        variants={fadeInUp}
                      >
                        <PropertyCard property={property} />
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <div className="text-center py-16">
                    <motion.div 
                      className="bg-blue-50 dark:bg-blue-900/30 w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    >
                      <BookmarkIcon className="w-10 h-10 text-blue-500 dark:text-blue-400" />
                    </motion.div>
                    <motion.h3 
                      className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.2 } }}
                    >
                      No tienes propiedades guardadas
                    </motion.h3>
                    <motion.p 
                      className="text-gray-500 dark:text-gray-400"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.3 } }}
                    >
                      Explora propiedades y guarda tus favoritas aquí
                    </motion.p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'history' && (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-6 md:p-8 border border-blue-100 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
                    <History className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
                    Historial de Visitas
                  </h2>
                </div>
                
                {data.history.length > 0 ? (
                  <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    {data.history.map((property) => (
                      <motion.div 
                        key={property.id} 
                        variants={fadeInUp}
                      >
                        <PropertyCard property={property} />
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <div className="text-center py-16">
                    <motion.div 
                      className="bg-blue-50 dark:bg-blue-900/30 w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    >
                      <History className="w-10 h-10 text-blue-500 dark:text-blue-400" />
                    </motion.div>
                    <motion.h3 
                      className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.2 } }}
                    >
                      No hay historial de visitas
                    </motion.h3>
                    <motion.p 
                      className="text-gray-500 dark:text-gray-400"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.3 } }}
                    >
                      Las propiedades que visites aparecerán aquí
                    </motion.p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'partners' && (
            <motion.div
              key="partners"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 p-6 md:p-8 border border-blue-100 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
                    Socios Favoritos
                  </h2>
                </div>
                
                {data.topPartners.length > 0 ? (
                  <PartnersCarousel partners={data.topPartners} />
                ) : (
                  <div className="text-center py-16">
                    <motion.div 
                      className="bg-blue-50 dark:bg-blue-900/30 w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    >
                      <Users className="w-10 h-10 text-blue-500 dark:text-blue-400" />
                    </motion.div>
                    <motion.h3 
                      className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.2 } }}
                    >
                      No tienes socios favoritos aún
                    </motion.h3>
                    <motion.p 
                      className="text-gray-500 dark:text-gray-400"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.3 } }}
                    >
                      Explora propiedades y califica a los vendedores para comenzar a construir tu red
                    </motion.p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ClientProfile;